import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Outlet, useLocation, useParams } from 'react-router';
import { clearOnsiteInterviews } from '~/actions/PermanentJobs';
import { Center, Screen, StepperIndicator } from '@tempmee/react';
import { CreateOnsiteInterviewSteps, TCreateOnsiteInterviewPageRoutes } from './Types';
import { CreatePermanentJobPostingSteps } from '../CreatePosting';
import { useBlockBackNavigation } from '~/hooks';

const BLOCKED_ROUTE_PATTERNS = [
  `/permanent-jobs/postings/:id/create/${CreatePermanentJobPostingSteps.BasicInformation}`,
  `/permanent-jobs/postings/:id/create/${CreatePermanentJobPostingSteps.JobDescription}`,
  `/permanent-jobs/postings/:id/create/${CreatePermanentJobPostingSteps.ReviewAndPayment}`,
  `/permanent-jobs/postings/:id/create/${CreatePermanentJobPostingSteps.WorkDetails}`,
];

export const CreateOnsiteInterviewPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { id } = useParams<TCreateOnsiteInterviewPageRoutes>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [location.pathname]);

  useEffect(() => {
    return () => {
      // Don't clear data if:
      // 1. Navigating to an edit route
      // 2. Still on the create page (internal navigation between steps)
      const isNavigatingToEdit =
        /\/permanent-jobs\/postings\/[^/]+\/onsite-interviews\/[^/]+\/edit$/.test(
          location.pathname,
        );
      const isStillOnCreate = /\/onsite-interviews\/create/.test(location.pathname);

      if (id && !isNavigatingToEdit && !isStillOnCreate) {
        dispatch(clearOnsiteInterviews(id));
      }
    };
  }, [location.pathname, dispatch, id]);

  // Prevent back navigation to creation routes
  useBlockBackNavigation({
    blockedRoutePatterns: BLOCKED_ROUTE_PATTERNS,
    redirectTo: '/',
  });

  const currentStepIndex = useMemo(() => {
    if (location.pathname.endsWith(CreateOnsiteInterviewSteps.Reviews)) {
      return 1;
    }

    return 0;
  }, [location.pathname]);

  return (
    <Screen variant="standard">
      <Screen.Header title="Add Onsite Interviews" />
      <Screen.Body>
        <Center py={4}>
          <StepperIndicator
            currentStep={currentStepIndex}
            steps={['Onsite Interviews', 'Review and Confirm']}
          />
        </Center>
        <Outlet />
      </Screen.Body>
    </Screen>
  );
};
